import { useNavigate, useParams } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Package } from "@/components/icons";
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  toast,
} from "@/components/kit";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { ImportOrderPhase13LineForm } from "@/modules/inventory/presentation/ui/ImportOrderPhase13Form";
import {
  appendLineToImportOrderDraft,
  peekImportOrderPhase13Draft,
} from "@/modules/inventory/presentation/lib/import-prefill-phase13";
import type { ProductVariantDTO } from "@/modules/products/domain/types";
import {
  useProductCategoriesQuery,
  useProductQuery,
  useUpdateProduct,
} from "../api/queries";
import { ProductForm } from "../ui/ProductForm";
import { ProductFormPageHeader } from "../ui/ProductFormPageHeader";
import { ProductVariantsPanel } from "../ui/ProductVariantsPanel";

export function ProductEditPage() {
  const { id } = useParams({ from: "/app/products/$id/edit" });
  const nav = useNavigate({ from: "/app/products/$id/edit" });
  const categories = useProductCategoriesQuery();
  const product = useProductQuery(id);
  const update = useUpdateProduct(id);
  const canManage = useActionPermission(["products:write"]);

  if (product.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-4">
          <Skeleton className="h-10 w-56 rounded-lg" />
          <Skeleton className="h-80 w-full rounded-2xl" />
        </Main>
      </>
    );
  }

  if (!product.data) {
    return (
      <>
        <Header />
        <Main>
          <div className="brand-card p-8">
            <Empty>
              <EmptyHeader>
                <EmptyTitle>ບໍ່ພົບສິນຄ້າ</EmptyTitle>
                <EmptyDescription>ສິນຄ້າອາດຖືກລຶບ ຫຼື ID ບໍ່ຖືກຕ້ອງ</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        </Main>
      </>
    );
  }

  const p = product.data;
  const importDraft = peekImportOrderPhase13Draft();

  function handleVariantCreatedForImport(variant: ProductVariantDTO) {
    const draft = peekImportOrderPhase13Draft();
    if (!draft) return;

    const line: ImportOrderPhase13LineForm = {
      variantId: variant.id,
      productName: p.name,
      variantName: variant.name,
      imageKey: variant.imageKey ?? p.imageKey ?? null,
      description: "",
      orderedQuantity: 1,
      unitPriceForeign: 0,
      stockUnitLabel: variant.stockUnitLabel,
      receiveUnitLabel: variant.receiveUnitLabel,
      receiveConversionFactor: Number(variant.receiveConversionFactor) || 1,
    };

    appendLineToImportOrderDraft(line);
    toast.success("ເພີ່ມສິນຄ້າໃນໃບສັ່ງ — ກັບໄປແກ້ໄຂຕໍ່");
    nav({ to: draft.returnTo });
  }

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <ProductFormPageHeader
          title={p.name}
          subtitle="ແກ້ໄຂຂໍ້ມູນສິນຄ້າ ແລະ ຕົວເລືອກ"
          onBack={() =>
            nav({
              to: importDraft?.returnTo ?? "/app/products",
            })
          }
          actions={
            <Button
              variant="outline"
              className="rounded-full border-secondary/40"
              onClick={() =>
                nav({ to: "/app/products/$id/stock", params: { id } })
              }
            >
              <Package data-icon="inline-start" />
              Lot & ສະຕັອກ
            </Button>
          }
        />

        {importDraft ? (
          <div className="rounded-xl border border-amber-600/30 bg-amber-500/10 px-4 py-3 text-sm">
            <p className="font-medium">ເພີ່ມຕົວເລືອກເພື່ອໃບສັ່ງນຳເຂົ້າ</p>
            <p className="text-muted-foreground mt-1 text-xs">
              ຫຼັງສ້າງ variant ລະບົບຈະເພີ່ມເຂົ້າໃບສັ່ງ ແລະ ພາກັບໄປອັດຕະໂນມັດ
            </p>
          </div>
        ) : null}

        <ProductForm
          categories={categories.data?.data ?? []}
          submitting={update.isPending}
          defaultValues={{
            categoryIds: p.categories.map((category) => category.id),
            name: p.name,
            slug: p.slug,
            description: p.description ?? "",
            imageKey: p.imageKey ?? "",
            lowStockThreshold: p.lowStockThreshold,
            isActive: p.isActive,
          }}
          onSubmit={async (vals) => {
            try {
              await update.mutateAsync(vals);
              toast.success("ອັບເດດສິນຄ້າສຳເລັດ");
            } catch {
              toast.error("ອັບເດດລົ້ມເຫຼວ");
            }
          }}
        />

        <ProductVariantsPanel
          productId={p.id}
          variants={p.variants}
          defaultLowStockThreshold={p.lowStockThreshold}
          canManage={!!canManage}
          onChanged={() => {
            void product.refetch();
          }}
          onVariantCreated={importDraft ? handleVariantCreatedForImport : undefined}
        />
      </Main>
    </>
  );
}
